import { Options } from '@/components/mi-dialog/MiDialog';
import { Country } from '@/models/Country';
import { Setting } from '@/models/Setting';
import { Breadcrumb } from '@/support/Breadcrumb';
import { Storage } from '@/core';
import Vue from 'vue';
import { Route } from 'vue-router';
import Vuex from 'vuex';
import { ProductFilterItem } from "@/components/CustomFilters/ProductFilters/ProductFilters";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    Auth: null,
    filters: [],
    router: {} as Router,
    selected: null,
    dialog: null,
    settings: [],
    breadcrumbs: [] as Breadcrumb[],
    lead: null,
    leadAnswers: null,
    productFilters: null,
    productPage: null,
    orderFilters: null,
    currentIpCountry: null,
    productSessionFilters: {} as ProductFilterItem,
    screenInfo: { screenSmallerThan: [], screenEqualsOrBiggerThan: [] } as ScreenInfo,
    isAppScrollbarForcedHidden: false,
    // The customer the stylist is composing for, so Maia can greet her by name.
    // Set by the webshop when it loads the contact; read by App, which is where
    // the chat widget lives so that it survives navigating to a product page.
    maiaCustomerName: '',
  },

  getters: {
    productFilters: state => {
      return state.productFilters;
    },
    orderFilters: state => {
      return state.orderFilters;
    },
    productPage: state => {
      return state.productPage;
    },
    currentIpCountry: state => {
      return state.currentIpCountry;
    },
    productSessionFilters: state => {
      return state.productSessionFilters as ProductFilterItem;
    },
    screenSmallerThan: state => {
      return state.screenInfo.screenSmallerThan;
    },
    screenEqualsOrBiggerThan: state => {
      return state.screenInfo.screenEqualsOrBiggerThan;
    },
    isAppScrollbarForcedHidden: state => {
      return state.isAppScrollbarForcedHidden;
    }
  },

  mutations: {
    USER_AUTHENTICATED: (state, payload) => {
      state.Auth = payload;
    },
    USER_DEAUTHENTICATED: (state, payload) => {
      if (state.Auth !== null && state.Auth !== undefined) {
        (state.Auth as any).logout();
      } else {
        window.location.href = '/';
      }

      state.Auth = null;
    },
    DRAWER_TOGGLE: (state: any, forcedState: boolean) => {
      state.drawer = !state.drawer;
    },
    MINI_TOGGLE: (state: any, forcedState: boolean) => {
      if (forcedState !== undefined) {
        state.miniNav = forcedState;
        return;
      }

      state.miniNav = !state.miniNav;
    },
    FILTERS_UPDATE: (state: any, payload) => {
      state.filters = { ...state.filters, ...payload };
    },
    SELECTED_CHANGE: (state: any, payload) => {
      state.selected = payload;
    },
    DIALOG_OPENED: (state: any, options: Options) => {
      state.dialog = options;
    },
    DIALOG_CLOSED: (state: any, options: null) => {
      state.dialog = null;
    },
    BREADCRUMBS_CHANGED: (state, payload: Breadcrumb[]) => {
      state.breadcrumbs = payload;
    },
    // TODO: delete this Sjerp
    COUNTRIES_LOADED: (state: any, payload: Country[]) => {
      state.countries = payload;
    },
    SETTINGS_LOADED: (state: any, settings: Setting[]) => {
      state.settings = settings;

      settings.forEach((setting: Setting) => {
        if (!setting.key) {
          return;
        }

        Storage.set(setting.key, `${setting.value}`);
      });
    },
    LEADANSWERS_UPDATE: (state: any, payload) => {
      state.leadAnswers = payload;
    },
    LEAD_UPDATE: (state: any, payload) => {
      state.lead = payload;
    },
    MAIA_CUSTOMER_UPDATE: (state: any, payload: string) => {
      state.maiaCustomerName = payload;
    },
    PRODUCTFILTERS_UPDATE: (state: any, payload) => {
      state.productFilters = payload;
    },
    PRODUCTPAGE_UPDATE: (state: any, payload) => {
      state.productPage = payload;
    },
    ORDERFILTERS_UPDATE: (state: any, payload: any) => {
      state.orderFilters = payload;
    },
    CURRENT_IP_COUNTRY: (state: any, payload: any) => {
      state.currentIpCountry = payload;
    },
    SET_PRODUCT_FILTERS: (state: any, productFilter: ProductFilterItem) => {
      const hasFilters = Object.keys(productFilter).length;

      const parsedProductFilters = state.productSessionFilters;

      state.productSessionFilters = hasFilters ? {...parsedProductFilters, productFilter} : parsedProductFilters;
    },
    SET_SCREEN_INFO: (state: any, payload: ScreenInfo) => {
      state.screenInfo = payload;
    },
    SET_APP_SCROLLBAR_FORCED_HIDDEN: (state: any, payload: boolean) => {
      state.isAppScrollbarForcedHidden = payload;
    }
  },

  actions: {
    userAuthenticated: (context, payload) => {
      context.commit('USER_AUTHENTICATED', payload);
    },
    userDeauthenticated: (context, payload) => {
      context.commit('USER_DEAUTHENTICATED', payload);
    },
    toggleDrawer: (context: any, payload: boolean) => {
      context.commit('DRAWER_TOGGLE', payload);
    },
    toggleMini: (context: any, payload: boolean) => {
      context.commit('MINI_TOGGLE', payload);
    },
    updateFilters: (context: any, payload: string) => {
      context.commit('FILTERS_UPDATE', payload);
    },
    selectedChanged: (context: any, payload: any) => {
      context.commit('SELECTED_CHANGE', payload);
    },
    openDialog: (context: any, options: Options) => {
      context.commit('DIALOG_OPENED', options);
    },
    closeDialog: (context: any, options: null = null) => {
      context.commit('DIALOG_CLOSED', options);
    },
    changeBreadcrumbs: (context: any, breadcrumbs: Breadcrumb[] = []) => {
      context.commit('BREADCRUMBS_CHANGED', breadcrumbs);
    },
    // TODO: DELETE
    loadedCountries: (context: any, payload: Country[]) => {
      context.commit('COUNTRIES_LOADED', payload);
    },
    loadedSettings: (context: any, settings: Setting[]) => {
      context.commit('SETTINGS_LOADED', settings);
    },
    updateLeadAnswers: (context: any, payload: any) => {
      context.commit('LEADANSWERS_UPDATE', payload);
    },
    updateLead: (context: any, payload: any) => {
      context.commit('LEAD_UPDATE', payload);
    },
    updateProductFilters: (context: any, payload: any) => {
      context.commit('PRODUCTFILTERS_UPDATE', payload);
    },
    updateOrderFilters: (context: any, payload: any) => {
      context.commit('ORDERFILTERS_UPDATE', payload)
    },
    updateProductPage: (context: any, payload: any) => {
      context.commit('PRODUCTPAGE_UPDATE', payload);
    },
    setCurrentIpCountry: (context: any, payload: any) => {
      context.commit('CURRENT_IP_COUNTRY', payload);
    },
    setProductFilters: (context: any, filters: ProductFilterItem) => {
      context.commit('SET_PRODUCT_FILTERS', filters);
    },
    setScreenInfo: (context: any, screenInfo: ScreenInfo) => {
      context.commit('SET_SCREEN_INFO', screenInfo);
    },
    setAppScrollbarForcedHidden: (context: any, payload: boolean) => {
      context.commit('SET_APP_SCROLLBAR_FORCED_HIDDEN', payload);
    },
  },
});

interface Router {
  previous: Route;
  current: Route;
}

interface RangeFilter {
  from: number | string;
  till: number | string;
}

export interface ScreenInfo {
  screenSmallerThan: CustomBreakpoint[];
  screenEqualsOrBiggerThan: CustomBreakpoint[];
}

//#region Types

export type CustomBreakpoint = 1225 | 1350 | 1450 | 1500 | 1550 | 1567;

//#endregion
